import { Location } from '@angular/common';
import { Directive, Input, OnDestroy, OnInit, WritableSignal, inject, signal } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormArray, FormControl, FormControlOptions, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';

export class SpecialFormControl<T = any> extends FormControl {
  public mapErrors: Record<string, string> = {};

  constructor(formState: T, opts?: FormControlOptions | null, asyncValidators?: AsyncValidatorFn | AsyncValidatorFn[] | null, mapErrors?: Record<string, string>) {
    const asyncVal = asyncValidators === null || asyncValidators === undefined ? undefined : asyncValidators;
    super(formState, opts ?? undefined, asyncVal);
    if (mapErrors) {
      this.mapErrors = { ...mapErrors };
    }
  }

  public getErrorMessage(): string | null {
    if (!this.errors) return null;
    const first = Object.keys(this.errors)[0];
    return this.mapErrors[first] ?? null;
  }
}

export type ControlValidation = {
  hasError: boolean;
  invalid: boolean;
  dirty: boolean;
  message: string;
  errors: string[];
};

export type GroupValidation<C> = Validation<C> & {
  invalid: boolean;
  message: string;
};

type Validation<T> = {
  [K in keyof T]: T[K] extends FormControl<any> ? ControlValidation : T[K] extends FormGroup<any> ? GroupValidation<T[K]['controls']> : T[K] extends FormArray<FormGroup<any>> ? Array<GroupValidation<T[K]['controls'][number]['controls']>> : never;
};

@Directive()
export class Crud<TCrud extends { [K in keyof TCrud]: AbstractControl<any, any> }> implements OnInit, OnDestroy {
  @Input()
  public id: string | null = null;

  public isUpdate = !!this.id;

  public isLoading: WritableSignal<boolean> = signal(true);

  public isSaving: WritableSignal<boolean> = signal(false);

  public validation: WritableSignal<GroupValidation<TCrud>> = signal({} as GroupValidation<TCrud>);

  private readonly location: Location = inject(Location);

  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);

  protected form = new FormGroup<TCrud>(<TCrud>{});

  private defaultMessages: Record<string, string> = {
    required: 'Campo é obrigatório',
    requiredTrue: 'Confirme esta opção',
    email: 'E-mail inválido',
    min: 'Valor mínimo é invalido',
    max: 'Valor máximo é invalido',
    minlength: 'Valor mínimo de caracteres é invalido',
    maxlength: 'Valor máximo de caracteres é invalido',
    pattern: 'Formato inválido',
    unique: 'Este valor já está em uso',
    mismatch: 'Os valores não conferem',
    invalid: 'Valor inválido',
    server: 'Não foi possível validar no servidor'
  };

  private subscription?: Subscription;

  public ngOnInit(): void {
    this.defineErrors();

    this.subscription = this.form.valueChanges.subscribe(() => {
      this.defineErrors();
    });

    if (!this.id) {
      const id = this.activatedRoute.snapshot.paramMap.get('id');
      this.id = id ? id : null;
    }

    this.isUpdate = !!this.id;
  }

  public ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  public return(): void {
    this.location.back();
  }

  public tap(): void {
    this.defineErrors();
  }

  public canSubmit(extraDisabled = false): boolean {
    return !this.form.invalid && !this.isLoading() && !this.isSaving() && !extraDisabled;
  }

  public guardSubmit(extraDisabled = false): boolean {
    if (!this.canSubmit(extraDisabled)) {
      if (this.form.invalid) this.form.markAllAsTouched();

      return false;
    }

    return true;
  }

  private defineErrors(): void {
    this.validation.set(this.processFormGroup(this.form));
  }

  private processFormGroup(form: FormGroup): any {
    const mainValidation = {} as any;

    for (const [key, control] of Object.entries(form.controls)) {
      mainValidation[key] = this.processForm(key, control);
    }

    return {
      ...mainValidation,
      invalid: form.invalid,
      message: this.getFirstErrorMessageDeep(mainValidation)
    };
  }

  private processFormArray(array: FormArray): any[] {
    const mainValidation: any[] = [];

    for (const [key, control] of Object.entries(array.controls)) {
      const validation = this.processForm(key, control);
      mainValidation.push(validation);
    }

    return mainValidation;
  }

  private processFormControl(key: string, abstractControl: AbstractControl): any {
    const keyErrors = Object.keys(abstractControl.errors || {});
    const mapErros = {
      ...this.defaultMessages,
      ...(abstractControl as SpecialFormControl).mapErrors
    };

    const errors: string[] = keyErrors.map(k => mapErros[k] || 'Campo inválido');
    const message = errors[0];

    return {
      hasError: abstractControl.invalid && abstractControl.dirty,
      invalid: abstractControl.invalid,
      dirty: abstractControl.dirty,
      message,
      errors
    };
  }

  private processForm(key: string, control: AbstractControl<any, any>): any {
    if (control instanceof FormGroup) return this.processFormGroup(control);

    if (control instanceof FormArray) return this.processFormArray(control);

    if (control instanceof FormControl) return this.processFormControl(key, control);
  }

  private getFirstErrorMessageDeep(validation: any): string {
    for (const [_, control] of Object.entries(validation)) {
      const baseControl = control as any;

      if (baseControl.invalid && baseControl.message) return baseControl.message;

      if (Array.isArray(baseControl)) {
        for (const c of baseControl) return this.getFirstErrorMessageDeep(c);
      }
    }

    return '';
  }
}
